import { Box, Chip } from '@mui/material';
import SortIcon from '@mui/icons-material/Sort';
import { useTranslation } from 'react-i18next';

/** The orders the recipe list can be sorted in. */
export type RecipeSort = 'name' | 'cookTime' | 'newest';

export type RecipeSortBarProps = {
  value: RecipeSort;
  onChange: (sort: RecipeSort) => void;
}

const SORT_OPTIONS: { value: RecipeSort; labelKey: string }[] = [
  { value: 'name', labelKey: 'sortByName' },
  { value: 'cookTime', labelKey: 'sortByCookTime' },
  { value: 'newest', labelKey: 'sortByNewest' },
];

/**
 * The sort rail that sits directly below the app bar on the recipe overview. Stays pinned to the
 * top of the scroll container while the list scrolls underneath it.
 */
const RecipeSortBar = (props: RecipeSortBarProps) => {
  const { t } = useTranslation();

  return (
    <Box
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 2,
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        px: 2,
        py: 1.25,
        backgroundColor: 'background.default',
        borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
        overflowX: 'auto',
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': { display: 'none' },
      }}
    >
      {SORT_OPTIONS.map((option) => {
        const selected = props.value === option.value;
        return (
          <Chip
            key={option.value}
            label={t(option.labelKey)}
            icon={selected ? <SortIcon sx={{ fontSize: 13 }} /> : undefined}
            variant={selected ? 'filled' : 'outlined'}
            onClick={() => props.onChange(option.value)}
            sx={{
              height: 30,
              borderRadius: '15px',
              fontWeight: 600,
              fontSize: '0.74rem',
              flexShrink: 0,
              cursor: 'pointer',
              backgroundColor: (theme) => selected
                ? (theme.palette.mode === 'light' ? theme.palette.secondary.main : theme.palette.primary.main)
                : 'transparent',
              color: (theme) => selected
                ? (theme.palette.mode === 'light' ? '#FFFFFF' : '#1C1208')
                : theme.palette.text.secondary,
              borderColor: (theme) => theme.palette.mode === 'light' ? '#E8D6BF' : theme.palette.divider,
              '& .MuiChip-icon': {
                color: 'inherit',
                marginLeft: '10px',
                marginRight: '-4px',
              },
              '&:hover': {
                backgroundColor: (theme) => selected
                  ? (theme.palette.mode === 'light' ? theme.palette.secondary.dark : '#D97706')
                  : (theme.palette.mode === 'light' ? 'rgba(217, 119, 6, 0.07)' : 'rgba(245, 158, 11, 0.08)'),
              },
            }}
          />
        );
      })}
    </Box>
  );
};

export default RecipeSortBar;
